/* eslint-disable  @typescript-eslint/no-explicit-any */

import { getFcmTokenIfGranted } from "helpers/firebase";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { io, Socket } from "socket.io-client";
import { userService } from "src/services";

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  isMarketClosed: boolean;
  connectionStatus: string;
  emit: (event: string, data?: any, callback?: (response: any) => void) => void;
  on: (event: string, callback: (data: any) => void) => void;
  off: (event: string, callback?: (data: any) => void) => void;
  disconnect: () => void;
  reconnect: () => void;
  connect: () => Socket;
}

const SocketContext = createContext<SocketContextType | null>(null);

interface SocketProviderProps {
  children: ReactNode;
  url?: string;
  authToken?: string | null;
  options?: object;
}

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({
  children,
  url = process.env.NEXT_PUBLIC_SOCKET_URL!,
}) => {
  const socketRef = useRef<Socket | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isMarketClosed, setIsMarketClosed] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] =
    useState<string>("disconnected");

  const connect = useCallback((): Socket => {
    if (socketRef.current?.connected) {
      return socketRef.current;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current.removeAllListeners();
    }

    const socketOptions = {
      transports: ["websocket"],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    };

    const newSocket = io(url, socketOptions);
    socketRef.current = newSocket;

    newSocket.on("connect", () => {
      setIsConnected(true);
      setConnectionStatus("connected");
      console.log("Socket connected successfully");

      const user = userService.userValue;
      if (user && user.token) {
        newSocket.emit("join_notifications", { access_token: user.token });
      }
    });

    newSocket.on("disconnect", (reason: string) => {
      setIsConnected(false);
      setConnectionStatus("disconnected");
      console.log("Socket disconnected:", reason);
    });

    newSocket.on("connect_error", (error: Error) => {
      setConnectionStatus("error");
      console.error("Connection error:", error);
    });

    newSocket.on("reconnecting", (attempt: number) => {
      setConnectionStatus("reconnecting");
      console.log(`Reconnecting attempt ${attempt}`);
    });

    newSocket.on("reconnect", (attempt: number) => {
      setConnectionStatus("connected");
      console.log(`Reconnected after ${attempt} attempts`);
    });

    newSocket.on("reconnect_failed", () => {
      setConnectionStatus("failed");
      console.error("Reconnection failed");
    });

    newSocket.on("new_notification", (data: any) => {
      if (
        document.visibilityState === "visible" &&
        window.location.pathname !== "/notifications"
      ) {
        window.dispatchEvent(
          new CustomEvent("onStackNotification", { detail: data }),
        );
      }
      window.dispatchEvent(new CustomEvent("onNewNotification"));
    });

    newSocket.on("market_status", (msg: { status: string }) => {
      if (msg && msg.status === "closed") {
        setIsMarketClosed(true);
      }
    });

    // Any live tick means the market is open again. These fire many times a second, so
    // the update has to be a no-op unless the flag actually changes — otherwise every
    // consumer of this context re-renders on every tick.
    const markMarketOpen = () =>
      setIsMarketClosed((closed) => (closed ? false : closed));

    newSocket.on("stock_update", markMarketOpen);
    newSocket.on("index_price_tick", markMarketOpen);

    setSocket(newSocket);

    return newSocket;
  }, [url]);

  const disconnect = useCallback((): void => {
    const s = socketRef.current;
    if (s) {
      s.disconnect();
      s.removeAllListeners();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
      setConnectionStatus("disconnected");
    }
  }, []);

  const reconnect = useCallback((): void => {
    disconnect();
    connect();
  }, [disconnect, connect]);

  useEffect(() => {
    const newSocket = connect();

    return () => {
      if (newSocket) {
        newSocket.disconnect();
        newSocket.removeAllListeners();
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(
    (event: string, data?: any, callback?: (response: any) => void): void => {
      const s = socketRef.current;
      if (!s?.connected) {
        console.warn("Cannot emit - socket not connected");
        return;
      }
      // Do not pass an undefined callback — socket.io serializes it as `null`
      // in the packet (e.g. 42["event", data, null]).
      if (typeof callback === "function") {
        s.emit(event, data, callback);
      } else if (data !== undefined) {
        s.emit(event, data);
      } else {
        s.emit(event);
      }
    },
    [],
  );

  const on = useCallback(
    (event: string, callback: (data: any) => void): void => {
      socketRef.current?.on(event, callback);
    },
    [],
  );

  const off = useCallback(
    (event: string, callback?: (data: any) => void): void => {
      const s = socketRef.current;
      if (s) {
        if (callback) {
          s.off(event, callback);
        } else {
          s.off(event);
        }
      }
    },
    [],
  );

  const fetchFcmToken = useCallback(async () => {
    try {
      const fcmToken = await getFcmTokenIfGranted();

      if (fcmToken) {
        localStorage.setItem("fcm_token", fcmToken);
      }

      await userService.syncFcmToken(fcmToken || undefined);
    } catch (error) {
      console.error("Error fetching FCM token:", error);
    }
  }, []);

  useEffect(() => {
    fetchFcmToken();
  }, [fetchFcmToken]);

  useEffect(() => {
    const fcmToken = localStorage.getItem("fcm_token");
    if (fcmToken && userService.userValue?.token) {
      userService.syncFcmToken(fcmToken);
    }
  }, []);

  const value: SocketContextType = useMemo(
    () => ({
      socket,
      isConnected,
      isMarketClosed,
      connectionStatus,
      emit,
      on,
      off,
      disconnect,
      reconnect,
      connect,
    }),
    [
      socket,
      isConnected,
      isMarketClosed,
      connectionStatus,
      emit,
      on,
      off,
      disconnect,
      reconnect,
      connect,
    ],
  );

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
